import React, { useState, useEffect } from "react";
import GenericTable from "../../../components/table";
import { Button, Grid, Box, Typography, Chip, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ProjectApi } from "../../../apis";

const columns = [
  { field: "id", headerName: "ID", sortable: true },
  { field: "name", headerName: "Name", sortable: true },
  { field: "description", headerName: "Description", sortable: true },
  {
    field: "phases",
    headerName: "Phases",
    renderCell: (params) => {
      const phases = params.row.phases?.map(p => p?.name).join(", ") || "No Phases";
      return <Typography variant="body2">{phases}</Typography>;
    }, sortable: true
  },
  {
    field: "status",
    headerName: "Status",
    renderCell: (params) => {
      return <Chip label={params.value} size="small" />;
    }, sortable: true
  },
  {
    field: "groups",
    headerName: "Groups", sortable: true
  }
];

function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("active");
  const navigate = useNavigate();
  const handleEdit = (row) => {
    if (row?.id) {
      navigate(`/admin/projects/${row.id}/edit`);
    }
  };

  const handleStatusChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const fetchProjects = async () => {
    try {
      const res = await ProjectApi.getAllProjects();
      const mapped = res.data.data.projects.map((project) => ({
        ...project,
        groups: project.groups || [], // Ensure groups is always an array
      }));
      // Default sort: newest/highest ID first
      mapped.sort((a, b) => Number(b.id) - Number(a.id));
      setProjects(mapped);
    } catch (error) {
      console.error("Error fetching projects:", error);
      setProjects([]); // Set projects to an empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await ProjectApi.deleteProject(id); // Replace with your API call
      fetchProjects(); // Refresh the projects list after deleting
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  // Custom filter component to pass to GenericTable
  const customFilters = (
    <>
      <Grid item xs={12} sm={4}></Grid>
      <Grid mb={4} item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Box sx={{ width: '100%' }}>
          <InputLabel
            htmlFor="status-filter"
            sx={{
              fontSize: 16,
              fontWeight: "bold",
              color: "black",
            }}
          >
            Filter by Status:
          </InputLabel>
          <FormControl fullWidth margin="dense" size="small">
            <Select
              id="status-filter"
              value={statusFilter}
              onChange={handleStatusChange}
              sx={{ width: "100%" }}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Grid>
    </>
  );

  return (
    <Box>
      <Grid container spacing={2} mt={2} mb={4}>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <Typography variant="h4" fontWeight={600}>
            Projects
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={6}
          lg={6}
          xl={6}
          display="flex"
          justifyContent="flex-end"
        >
          <Button variant="contained" color="primary" onClick={() => navigate("/admin/projects/add")}>
            Add Project
          </Button>
        </Grid>
      </Grid>

      {loading ? (
        <Typography variant="h6">Loading...</Typography>
      ) : (
        <GenericTable
          data={projects}
          columns={columns}
          is_group_selection_shown={true}
          onEdit={handleEdit}
          onDelete={handleDelete} // Pass the delete function
          customFilters={customFilters}
          statusFilter={statusFilter}
          statusField="status"
          inlineEdit={false}
        // You can add more functionalities like onAssign here if needed
        />
      )}
    </Box>
  );
}

export default Projects;
